import { fetchAuthSession } from '@aws-amplify/auth';
import { readSupportAccessSessionId } from '@chiroup/client-core/functions/ChiroUpAPI';
import { sendSyncOperation } from '@chiroup/client-core/functions/SyncServerAPI';
import { MILLISECONDS } from '@chiroup/core/constants/time';
import { sanitizeEncounterInfo } from '@chiroup/core/functions/sanitizeEncounterInfo';
import { buildSyncDocumentName } from '@chiroup/core/functions/syncDocumentName';
import {
  EncounterSignatureDetails,
  Visit,
  VisitDetail,
} from '@chiroup/core/types/PatientVisit.type';
import { SyncAuthenticationEnvelope } from '@chiroup/core/types/SyncAuthentication.type';
import { SyncOperation } from '@chiroup/core/types/SyncOperation.type';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';
import { createDebouncedFieldWrites, DEBOUNCED_WRITE_MAX_WAIT_MS } from './debouncedFieldWrites';
import { resolveVisitDetail } from './resolveVisitDetail';

export interface CollaborativeEncounterConfig {
  encounterId: string;
  clinicId: number;
  websocketUrl: string;
  documentName?: string;
  /**
   * Database user ID (User.ID from the users table)
   */
  userId?: string;
  /**
   * Initial visit snapshot used to seed the collaborative document
   * the first time it is created (before any Yjs state exists).
   */
  initialVisit?: Visit | null;
}

const buildSyncAuthenticationToken = (jwt: string): string => {
  const supportAccessSessionId = readSupportAccessSessionId();

  if (!supportAccessSessionId) return jwt;

  const envelope: SyncAuthenticationEnvelope = { jwt, supportAccessSessionId };

  return JSON.stringify(envelope);
};

export type ModalitySavingAction = 'complete' | 'skip' | 'delete' | 'edit' | 'add';

// How long after a keystroke a controlled note field ignores Yjs echoes
// (stale echoes would revert keystrokes / jump the cursor). Covers the
// debounce maxWait plus echo round trip.
export const LOCAL_EDIT_ECHO_WINDOW_MS = DEBOUNCED_WRITE_MAX_WAIT_MS + 500;

export interface AwarenessUser {
  userId: string;
  userName?: string;
  timestamp: number;
  surveyModalOpen?: {
    complaintId: string;
  };
  bodyPickerOpen?: {
    complaintId: string;
  };
  addingComplaint?: boolean;
  modalitiesSaving?: boolean;
  savingModalityId?: string | null;
  savingAction?: ModalitySavingAction | null;
  editingModalityId?: string | null;
  /** Set to true when user is actively editing exercises (prevents concurrent editing) */
  editingExercises?: boolean;
  /** Set to true when user is actively editing pointing (prevents concurrent editing) */
  editingPointing?: boolean;
  /** Timestamp for editingPointing - used for conflict resolution */
  editingPointingTimestamp?: number;
  /** Set to true when user has Import & Point modal open */
  importAndPointModalOpen?: boolean;
  /** Set to true when user is using SALT button */
  usingSalt?: boolean;
}

/**
 * Metadata stored in the Yjs document when an encounter is deleted.
 * This is broadcast to all connected clients so they can be notified.
 * Only set after successful backend deletion.
 */
export interface EncounterDeletionMetadata {
  deletedBy: string;
  deletedByClientId: number;
  deletedByName: string;
  deletedAt: number;
}

/**
 * Metadata stored in the Yjs document when an encounter is signed/unsigned.
 * This is broadcast to all connected clients so they can immediately lock/unlock
 * the UI and trigger a refetch of API-authoritative fields.
 *
 * This does NOT replace the REST API as the source of truth for signature state.
 */
export enum EncounterSignatureAction {
  Sign = 'sign',
  Unsign = 'unsign',
}

export interface EncounterSignatureMetadata {
  action: EncounterSignatureAction;
  byUserId: string;
  byClientId: number;
  byName: string;
  timestamp: number;
  signedAt?: number | null;
}

const getRealTimeSignedState = (args: {
  apiSigned: Visit['signed'] | undefined;
  apiSignedAt: Visit['signedAt'] | undefined;
  yjsSigned: unknown;
  yjsSignedAt: unknown;
  signatureMeta?: EncounterSignatureMetadata;
}): {
  effectiveSigned: Visit['signed'] | undefined;
  effectiveSignedAt: Visit['signedAt'] | undefined;
} => {
  const { apiSigned, apiSignedAt, yjsSigned, yjsSignedAt, signatureMeta } = args;
  const SIGNATURE_EVENT_OVERRIDE_TTL_MS = MILLISECONDS.inSeconds(20); // 20 second window for signature event override

  let effectiveSigned = apiSigned;
  let effectiveSignedAt = apiSignedAt;

  // address 'clock-skew' (if the 'signer' clock is ahead, age becomes negative).
  // override lasts AT MOST the TTL from receiving client's perspective.
  const signatureEventAgeMs = signatureMeta ? Math.max(0, Date.now() - signatureMeta.timestamp) : 0;
  const signatureEventOverrideIsFresh =
    !!signatureMeta && signatureEventAgeMs < SIGNATURE_EVENT_OVERRIDE_TTL_MS;

  if (signatureEventOverrideIsFresh && signatureMeta) {
    if (signatureMeta.action === EncounterSignatureAction.Sign) {
      effectiveSigned = apiSigned || (yjsSigned as Visit['signed'] | undefined);
      effectiveSignedAt =
        apiSignedAt ||
        (yjsSignedAt as Visit['signedAt'] | undefined) ||
        (signatureMeta.signedAt as Visit['signedAt'] | undefined);
    } else if (signatureMeta.action === EncounterSignatureAction.Unsign) {
      effectiveSigned = null;
      effectiveSignedAt = null;
    }
  }

  return { effectiveSigned, effectiveSignedAt };
};

export interface CollaborativeEncounterState {
  provider: HocuspocusProvider | null;
  ydoc: Y.Doc | null;
  encounterData: Y.Map<any> | null;
  visitForm: any;
  /**
   * The Yjs doc's own `detail`, regardless of signed state. `visitForm.detail`
   * serves the frozen Encounter row once a note is signed (resolveVisitDetail),
   * which is right for the SOAP Detail display but hides the doc from the
   * care-plan widgets that use `detail.*` as their live co-edit channel
   * (modality Time/Intensity edits are Yjs-only until Complete).
   */
  liveDetail: Visit['detail'] | undefined;
  isConnected: boolean;
  isSynced: boolean;
  /**
   * Indicates that local Yjs data is available for editing.
   * True when encounterData is initialized (even if sync server is offline).
   * Use this instead of isConnected for enabling/disabling UI inputs.
   */
  hasLocalData: boolean;
  error: Error | null;
  activeUsers: Set<string>;
  awarenessStates: Map<number, { user?: AwarenessUser }>;
  myClientId: number | null;
}

export interface CollaborativeEncounterValue extends CollaborativeEncounterState {
  updateField: (key: string, value: any) => void;
  /**
   * Debounced write for per-keystroke text (trailing 300ms, max wait 1s).
   *
   * `producer` runs at flush time: read live state via getField (visitForm
   * is stale by then) and return undefined to skip the write.
   *
   * Rapid calls with the same `coalesceKey` (default: `key`) collapse into
   * one write, latest call wins. Sub-fields sharing one Yjs key (e.g.
   * notes.subjective vs notes.objective) need distinct coalesceKeys so one
   * pending edit can't drop another.
   */
  updateFieldDebounced: (key: string, producer: () => any, coalesceKey?: string) => void;
  /**
   * Flush all pending debounced writes to Yjs now. Call before anything that
   * reads note content from Yjs or the sync server.
   */
  flushPendingWrites: () => void;
  updateDetailField: (key: keyof VisitDetail, value: unknown) => void;
  getField: (key: string) => any;
  deleteField: (key: string) => void;
  getAllData: () => Record<string, any>;
  subscribeToField: (key: string, callback: (value: any) => void) => () => void;
  setAwarenessField: (key: string, value: any) => void;
  patchInitialVisit: (patch: Partial<Visit>) => void;
  reconnect: () => void;
  disconnect: () => void;
  sendOperation: <T = unknown>(args: {
    operation: Omit<SyncOperation, 'encounterId' | 'clinicId' | 'patientId'> & {
      encounterId?: string;
      clinicId?: number;
      patientId?: string;
    };
    fallback: () => Promise<T>;
  }) => Promise<T>;
}

export function useCollaborativeEncounter(
  config: CollaborativeEncounterConfig
): CollaborativeEncounterValue {
  const {
    clinicId,
    encounterId,
    websocketUrl,
    documentName = 'encounter',
    initialVisit,
    userId,
  } = config;

  const [state, setState] = useState<CollaborativeEncounterState>({
    provider: null,
    ydoc: null,
    encounterData: null,
    visitForm: null,
    liveDetail: undefined,
    isConnected: false,
    isSynced: false,
    hasLocalData: false,
    error: null,
    activeUsers: new Set(),
    awarenessStates: new Map(),
    myClientId: null,
  });

  const providerRef = useRef<HocuspocusProvider | null>(null);
  const ydocRef = useRef<Y.Doc | null>(null);
  const initialVisitRef = useRef<Visit | null | undefined>(initialVisit);
  // Track connection failure count - accessible from both useEffect and reconnect callback
  const failureCountRef = useRef(0);
  // Cache the auth token to avoid excessive fetchAuthSession calls
  // which can cause session invalidation/logout issues
  const tokenCacheRef = useRef<{ token: string; expiresAt: number } | null>(null);
  // Random provider identifier used only to group repeated authentication failures.
  // It carries no user, clinic, patient, or encounter information.
  const authDiagnosticIdRef = useRef<string | null>(null);

  if (!authDiagnosticIdRef.current) {
    authDiagnosticIdRef.current = crypto.randomUUID();
  }

  // Track whether we've already seeded care plan data for this encounter
  const carePlanSeededRef = useRef<string | null>(null);

  // Keep initialVisitRef updated when initialVisit changes (from API loading)
  // Seed bodypicker and care plan data from API into Yjs when available
  useEffect(() => {
    const prevVisit = initialVisitRef.current;
    initialVisitRef.current = initialVisit;

    // Only seed data when we have both API data and Yjs is ready
    const needsRun =
      initialVisit &&
      state.encounterData &&
      (!prevVisit || prevVisit !== initialVisit || carePlanSeededRef.current !== encounterId);

    if (needsRun && state.encounterData) {
      const data = state.encounterData.toJSON();
      const apiVisit = initialVisit;

      let mergedEncounterInfo = data.encounterInfo || apiVisit?.encounterInfo;
      let needsYjsUpdate = false;

      // Merge bodypicker data from API into Yjs complaints
      if (data.encounterInfo?.complaints?.length && apiVisit?.encounterInfo?.complaints?.length) {
        const apiComplaintsById = new Map(
          apiVisit.encounterInfo.complaints.map((c: any) => [c.id, c])
        );

        const mergedComplaints = data.encounterInfo.complaints.map((yjsComplaint: any) => {
          const apiComplaint = apiComplaintsById.get(yjsComplaint.id);

          if (!yjsComplaint.bodypicker && apiComplaint?.bodypicker) {
            needsYjsUpdate = true;

            return {
              ...yjsComplaint,
              bodypicker: apiComplaint.bodypicker,
              bodypickerDerived: apiComplaint.bodypickerDerived,
            };
          }

          return yjsComplaint;
        });

        mergedEncounterInfo = {
          ...data.encounterInfo,
          complaints: mergedComplaints,
        };

        if (needsYjsUpdate) {
          state.encounterData.set(
            'encounterInfo',
            sanitizeEncounterInfo(mergedEncounterInfo as Record<string, unknown>)
          );
        }
      }

      // Seed care plan fields from API if Yjs doesn't have them
      if (apiVisit?.encounterInfo) {
        const yjsEncounterInfo = data.encounterInfo || {};
        // Treat `visitTimes: []` in Yjs as "missing" — there is no UI affordance
        // to clear the schedule, so an empty array is almost always a stale seed
        // from before the API response arrived. Prefer API when Yjs is empty.
        const yjsHasPopulatedVisitTimes =
          Array.isArray(yjsEncounterInfo.visitTimes) && yjsEncounterInfo.visitTimes.length > 0;
        // User-cleared sentinel suppresses API backfill; stale empties don't.
        const yjsVisitTimesUserCleared = yjsEncounterInfo.visitTimesClearedByUser === true;
        const shouldUseYjsVisitTimes = yjsHasPopulatedVisitTimes || yjsVisitTimesUserCleared;
        mergedEncounterInfo = {
          ...mergedEncounterInfo,
          goal: yjsEncounterInfo.goal ?? apiVisit.encounterInfo.goal,
          visitNumber:
            yjsEncounterInfo.visitNumber ??
            apiVisit.encounterInfo.visitNumber ??
            apiVisit?.visitNumber,
          nextReEval: yjsEncounterInfo.nextReEval ?? apiVisit.encounterInfo.nextReEval,
          visitTimes: shouldUseYjsVisitTimes
            ? Array.isArray(yjsEncounterInfo.visitTimes)
              ? yjsEncounterInfo.visitTimes
              : []
            : apiVisit.encounterInfo.visitTimes,
          notes: yjsEncounterInfo.notes ?? apiVisit.encounterInfo.notes,
          nextRequestedEncounterDate:
            yjsEncounterInfo.nextRequestedEncounterDate ??
            apiVisit.encounterInfo.nextRequestedEncounterDate,
          totalVisits: yjsEncounterInfo.totalVisits ?? apiVisit.encounterInfo.totalVisits,
          // surveysToDocument is API-authoritative: getFullEncounter computes it
          // from the provider's SOAP preferences while preserving any user-toggled
          // values already saved. Yjs starts with {} from visit creation and would
          // cause all "Document Survey" checkboxes to default to unchecked.
          surveysToDocument: apiVisit.encounterInfo.surveysToDocument,
          soapNoteDetailPreference: apiVisit.encounterInfo.soapNoteDetailPreference,
        };

        // A user-cleared sentinel counts as "Yjs has care plan intent" so the
        // reseed below won't clobber a deliberate empty schedule on a fresh doc.
        const yjsNeedsCarePlanSeed =
          !data.encounterInfo ||
          (yjsEncounterInfo.goal === undefined &&
            yjsEncounterInfo.nextReEval === undefined &&
            !yjsHasPopulatedVisitTimes &&
            !yjsVisitTimesUserCleared &&
            yjsEncounterInfo.notes === undefined &&
            yjsEncounterInfo.totalVisits === undefined);

        // Separately backfill visitTimes when API has data but Yjs is empty —
        // the full-reseed above only fires when every care plan field is unset,
        // so a partially-seeded Yjs doc (e.g. notes='', visitTimes=[]) would
        // otherwise stay empty forever.
        const apiHasPopulatedVisitTimes =
          Array.isArray(apiVisit.encounterInfo.visitTimes) &&
          apiVisit.encounterInfo.visitTimes.length > 0;

        if (yjsNeedsCarePlanSeed && apiVisit.encounterInfo) {
          mergedEncounterInfo = {
            ...mergedEncounterInfo,
            goal: apiVisit.encounterInfo.goal,
            nextReEval: apiVisit.encounterInfo.nextReEval,
            visitTimes: yjsVisitTimesUserCleared ? [] : apiVisit.encounterInfo.visitTimes,
            notes: apiVisit.encounterInfo.notes,
            totalVisits: apiVisit.encounterInfo.totalVisits,
            nextRequestedEncounterDate: apiVisit.encounterInfo.nextRequestedEncounterDate,
          };

          state.encounterData.set(
            'encounterInfo',
            sanitizeEncounterInfo(mergedEncounterInfo as Record<string, unknown>)
          );
        } else if (
          !yjsHasPopulatedVisitTimes &&
          !yjsVisitTimesUserCleared &&
          apiHasPopulatedVisitTimes
        ) {
          // Backfill from API only when Yjs is stale-empty, not user-cleared.
          state.encounterData.set(
            'encounterInfo',
            sanitizeEncounterInfo(mergedEncounterInfo as Record<string, unknown>)
          );
        }
      } else if (apiVisit?.visitNumber) {
        mergedEncounterInfo = {
          ...mergedEncounterInfo,
          visitNumber: apiVisit.visitNumber,
        };
      }

      // Merge complaintSummaries - API is authoritative for survey completion status
      const yjsSummaries = data.complaintSummaries || {};
      const apiSummaries = apiVisit?.complaintSummaries || {};
      let needsComplaintSummariesYjsUpdate = false;
      const mergedComplaintSummaries = { ...yjsSummaries };

      for (const [complaintId, apiSummary] of Object.entries(apiSummaries)) {
        const yjsSummary = yjsSummaries[complaintId];
        const typedApiSummary = apiSummary as { placeholder?: boolean } | undefined;

        if (!yjsSummary) {
          mergedComplaintSummaries[complaintId] = apiSummary;
          needsComplaintSummariesYjsUpdate = true;
        } else if (typedApiSummary?.placeholder === false && yjsSummary?.placeholder === true) {
          mergedComplaintSummaries[complaintId] = apiSummary;
          needsComplaintSummariesYjsUpdate = true;
        } else {
          // Backfill behavesAs/subjectiveTemplate from API if Yjs is missing them
          const yjsAny = yjsSummary as unknown as Record<string, unknown>;
          const apiAny = apiSummary as unknown as Record<string, unknown>;

          if (!yjsAny.behavesAs && apiAny.behavesAs) {
            yjsAny.behavesAs = apiAny.behavesAs;
            needsComplaintSummariesYjsUpdate = true;
          }

          if (!yjsAny.subjectiveTemplate && apiAny.subjectiveTemplate) {
            yjsAny.subjectiveTemplate = apiAny.subjectiveTemplate;
            needsComplaintSummariesYjsUpdate = true;
          }
        }
      }

      if (needsComplaintSummariesYjsUpdate) {
        state.encounterData.set('complaintSummaries', mergedComplaintSummaries);
      }

      // Seed version from API into Yjs if missing so subsequent observer-triggered
      // buildVisitForm() calls have it immediately (prevents V1/V2 UI flash).
      if (apiVisit?.version !== undefined && !('version' in data)) {
        state.encounterData.set('version', apiVisit.version);
      }

      const topLevelVisitNumber = mergedEncounterInfo?.visitNumber ?? apiVisit?.visitNumber;
      carePlanSeededRef.current = encounterId;

      // The API provides the authoritative source of truth for 'signed' state,
      // but we support a temporary 'signature event override' so encounters in other clients lock/unlock immediately while we refetch the API state.
      const { effectiveSigned, effectiveSignedAt } = getRealTimeSignedState({
        apiSigned: apiVisit?.signed,
        apiSignedAt: apiVisit?.signedAt,
        yjsSigned: data.signed,
        yjsSignedAt: data.signedAt,
        signatureMeta: data._signature as EncounterSignatureMetadata | undefined,
      });

      // Build visitForm from merged data — API data arrived, safe to show UI
      setState(prev => ({
        ...prev,
        hasLocalData: true,
        liveDetail: data.detail,
        visitForm: {
          ...apiVisit,
          ...data,
          id: encounterId,
          encounterInfo: mergedEncounterInfo,
          visitNumber: topLevelVisitNumber,
          complaintSummaries: mergedComplaintSummaries,
          detail: resolveVisitDetail({ apiVisit, yjsVisit: data }),
          // API-authoritative fields (not stored in Yjs as source of truth)
          version: apiVisit?.version ?? data.version,
          signed: effectiveSigned,
          signedAt: effectiveSignedAt,
          signedByUserId: apiVisit?.signedByUserId ?? null,
          signatureDetails: EncounterSignatureDetails.fromJSON(apiVisit?.signatureDetails),
          surveys: apiVisit?.surveys,
          inProgressComplaintSurveys: apiVisit?.inProgressComplaintSurveys,
          interimSurveyId: apiVisit?.interimSurveyId,
          reevaluationSurveyId: apiVisit?.reevaluationSurveyId,
          // Yjs-first, same pattern as `_signature` above for signed/signedAt:
          // a collaborative op (reset-care-plan / cancel-reset-care-plan) repoints
          // this mid-session and broadcasts the new chain root through Yjs so other
          // connected clients see it without waiting for a REST refetch. Fall back
          // to REST only when Yjs has no value yet.
          carePlanId: data.carePlanId ?? apiVisit?.carePlanId,
          lifetimeVisits: apiVisit?.lifetimeVisits,
          recordings: apiVisit?.recordings,
        },
      }));
    }
  }, [initialVisit, state.encounterData, encounterId]);

  // Initialize provider and Yjs document
  useEffect(() => {
    let provider: HocuspocusProvider | null = null;
    let ydoc: Y.Doc | null = null;
    let observerUnsubscribe: (() => void) | null = null;
    const MAX_FAILURES = 3;
    let isCleanedUp = false; // Prevent double-initialization in StrictMode/HMR
    // Max consecutive close events before giving up. Auth failures still use
    // the lower MAX_FAILURES threshold since they indicate a real problem.
    const MAX_CLOSE_FAILURES = 10;

    // Reset failure count on new initialization
    failureCountRef.current = 0;

    const cleanup = () => {
      // Flush pending debounced writes before the provider is destroyed so
      // the last ≤1s of typing still reaches the sync server. Best-effort:
      // cleanup can't await a server ack — the flush broadcasts into the
      // still-open socket synchronously, which survives the close in
      // practice, but a dead socket at teardown loses the frame.
      flushPendingWrites();

      if (observerUnsubscribe) {
        observerUnsubscribe();
      }

      if (provider) {
        provider.destroy();
      }

      if (ydoc) {
        ydoc.destroy();
      }

      providerRef.current = null;
      ydocRef.current = null;
    };

    const doIt = async () => {
      // Prevent initialization if cleanup already ran (StrictMode/HMR)
      if (isCleanedUp) {
        return;
      }

      try {
        const session = await fetchAuthSession();

        // StrictMode may clean up this effect while authentication is pending.
        // Never publish a Y.Map from that destroyed provider into React state.
        if (isCleanedUp) {
          return;
        }

        const idToken = session.tokens?.idToken;
        const token = idToken?.toString();
        const cognitoSub = idToken?.payload?.sub as string | undefined;
        const effectiveUserId = userId || cognitoSub;

        if (!clinicId || !encounterId || !websocketUrl || !token || !effectiveUserId) {
          const missing = [
            !clinicId && 'clinic',
            !encounterId && 'encounter',
            !websocketUrl && 'sync URL',
            !token && 'session token',
            !effectiveUserId && 'user',
          ].filter(Boolean);

          setState(prev => ({
            ...prev,
            error: new Error(`Encounter sync is missing: ${missing.join(', ')}`),
          }));

          return;
        }

        if (idToken?.payload?.exp) {
          tokenCacheRef.current = {
            token,
            expiresAt: (idToken.payload.exp as number) * 1000,
          };
        }

        // Create Yjs document
        ydoc = new Y.Doc();
        ydocRef.current = ydoc;

        // Create Hocuspocus provider with connection limits
        provider = new HocuspocusProvider({
          url: `${websocketUrl}/encounter/${encounterId}?clientInstanceId=${authDiagnosticIdRef.current}&clientVersion=${encodeURIComponent(import.meta.env.VITE_VERSION || 'unknown')}`,
          name: buildSyncDocumentName({ clinicId, encounterId }),
          document: ydoc,
          // Pass a function to fetch token on each connection/reconnection attempt
          // Uses caching to avoid excessive fetchAuthSession calls which can cause logout issues
          token: async () => {
            const now = Date.now();
            const cached = tokenCacheRef.current;
            // Use cached token if it's valid for at least 60 more seconds
            const TOKEN_BUFFER_MS = 60 * 1000;

            if (cached && cached.expiresAt - now > TOKEN_BUFFER_MS) {
              return buildSyncAuthenticationToken(cached.token);
            }

            // Need to refresh the token
            const freshSession = await fetchAuthSession();
            const freshIdToken = freshSession.tokens?.idToken;
            const tokenString = freshIdToken?.toString() || '';

            if (tokenString && freshIdToken?.payload?.exp) {
              // Cache the token with its expiration time (exp is in seconds, convert to ms)
              tokenCacheRef.current = {
                token: tokenString,
                expiresAt: (freshIdToken.payload.exp as number) * 1000,
              };
            }

            return buildSyncAuthenticationToken(tokenString);
          },
          // Poll server for updates every 500ms to ensure sync
          // This helps when WebSocket messages are delayed through ALB
          forceSyncInterval: 500,

          // Connection callbacks
          onConnect: () => {
            failureCountRef.current = 0; // Reset failure count on successful connection
            // Get our own client ID from the awareness
            const myClientId = provider?.awareness?.clientID ?? null;
            setState(prev => ({ ...prev, isConnected: true, error: null, myClientId }));
          },

          onDisconnect: () => {
            setState(prev => ({ ...prev, isConnected: false }));
          },

          onSynced: () => {
            setState(prev => ({ ...prev, isSynced: true }));
          },

          onAuthenticationFailed: ({ reason }) => {
            failureCountRef.current++;
            const error = new Error(`Authentication failed: ${reason}`);
            setState(prev => ({ ...prev, error }));

            // Stop trying after max failures
            if (failureCountRef.current >= MAX_FAILURES && provider) {
              provider.disconnect();
            }
          },

          onClose: () => {
            failureCountRef.current++;

            // Use a higher threshold for close events (vs auth failures) because
            // deployments cause legitimate rapid disconnects as the old ECS task
            // drains. The Hocuspocus provider's built-in reconnect with backoff
            // handles the retry timing — we just need to not give up too early.
            if (failureCountRef.current >= MAX_CLOSE_FAILURES && provider) {
              const error = new Error('Connection failed after multiple attempts.');
              setState(prev => ({ ...prev, error }));
              provider.disconnect();
            }
          },

          onStatus: () => {
            // Status updates handled silently
          },

          // Awareness for tracking active users and their state (e.g., who has modal open)
          // Optimized to avoid unnecessary re-renders by comparing state
          onAwarenessUpdate: ({ states }) => {
            setState(prev => {
              // Build new state
              const newActiveUsers = new Set<string>();
              const newAwarenessMap = new Map<number, { user?: AwarenessUser }>();

              for (const awarenessState of states) {
                const stateClientId = awarenessState.clientId;
                const stateUser = awarenessState.user as AwarenessUser | undefined;

                if (stateUser?.userId) {
                  newActiveUsers.add(stateUser.userId);
                }

                newAwarenessMap.set(stateClientId, { user: stateUser });
              }

              // Check if awareness actually changed (deep comparison)
              const prevUsers = prev.activeUsers;
              const prevStates = prev.awarenessStates;

              // Quick size check
              if (
                prevUsers.size !== newActiveUsers.size ||
                prevStates.size !== newAwarenessMap.size
              ) {
                return { ...prev, activeUsers: newActiveUsers, awarenessStates: newAwarenessMap };
              }

              // Check if user IDs changed
              let usersChanged = false;

              for (const activeUserId of newActiveUsers) {
                if (!prevUsers.has(activeUserId)) {
                  usersChanged = true;
                  break;
                }
              }

              // Check if awareness states actually changed (compare JSON to avoid timestamp-only updates)
              let statesChanged = false;

              if (!usersChanged) {
                for (const [clientId, newState] of newAwarenessMap) {
                  const prevState = prevStates.get(clientId);
                  // Only compare fields that matter for UI (not timestamp)
                  const prevUser = prevState?.user;
                  const newUser = newState.user;

                  if (!prevUser && !newUser) continue;

                  if (!prevUser || !newUser) {
                    statesChanged = true;
                    break;
                  }

                  // Compare meaningful fields (exclude timestamp to reduce re-renders)
                  if (
                    prevUser.editingExercises !== newUser.editingExercises ||
                    prevUser.editingPointing !== newUser.editingPointing ||
                    prevUser.editingPointingTimestamp !== newUser.editingPointingTimestamp ||
                    prevUser.modalitiesSaving !== newUser.modalitiesSaving ||
                    prevUser.surveyModalOpen?.complaintId !==
                      newUser.surveyModalOpen?.complaintId ||
                    prevUser.bodyPickerOpen?.complaintId !== newUser.bodyPickerOpen?.complaintId ||
                    prevUser.addingComplaint !== newUser.addingComplaint ||
                    prevUser.savingModalityId !== newUser.savingModalityId ||
                    prevUser.editingModalityId !== newUser.editingModalityId ||
                    prevUser.importAndPointModalOpen !== newUser.importAndPointModalOpen ||
                    prevUser.usingSalt !== newUser.usingSalt
                  ) {
                    statesChanged = true;
                    break;
                  }
                }
              }

              // Only update if something actually changed
              if (usersChanged || statesChanged) {
                return { ...prev, activeUsers: newActiveUsers, awarenessStates: newAwarenessMap };
              }

              // No change, return previous state to avoid re-render
              return prev;
            });
          },
        });
        providerRef.current = provider;

        // Get the shared map for encounter data
        const encounterData = ydoc.getMap('encounterInfo');

        // Seed initial data from the non-collaborative visit if this is a brand-new document
        if (encounterData.size === 0 && initialVisit) {
          // Exclude visitNumber from encounterInfo seed - it's server-calculated and
          // may be stale at seed time. We'll get it from API on merge, and user changes
          // will be stored in Yjs thereafter.
          const seedEncounterInfo = initialVisit.encounterInfo
            ? (() => {
                const { visitNumber: _visitNumber, ...rest } = initialVisit.encounterInfo;

                return sanitizeEncounterInfo(rest as Record<string, unknown>);
              })()
            : undefined;

          const seed: Partial<Visit> = {
            clinician: initialVisit.clinician,
            visitDate: initialVisit.visitDate,
            // signed/signedAt intentionally excluded — these are workflow state
            // managed by the REST API, not collaborative content. Including them
            // in YJS causes stale signed=null to overwrite the DB truth.
            encounterInfo: seedEncounterInfo,
            note: initialVisit.note,
            unifiedObjective: initialVisit.unifiedObjective,
            soapCustomEditWarning: initialVisit.soapCustomEditWarning,
            plan: initialVisit.plan,
            planWizardCategory: initialVisit.planWizardCategory,
            // Additional fields needed for EncounterHeader
            soapTemplate: initialVisit.soapTemplate,
            trackId: initialVisit.trackId,
            trackName: initialVisit.trackName,
            clinicId: initialVisit.clinicId,
            patientId: initialVisit.patientId,
            hasAppointment: initialVisit.hasAppointment,
            // Complaint summaries from patient surveys
            complaintSummaries: initialVisit.complaintSummaries,
          };

          Object.entries(seed).forEach(([key, value]) => {
            if (value !== undefined) {
              // Serialize to plain JSON to avoid Yjs "Unexpected content type" errors
              // Yjs can't handle Date objects, class instances, or certain nested structures directly
              try {
                const serialized = JSON.parse(JSON.stringify(value));
                encounterData.set(key, serialized);
              } catch (e) {}
            }
          });
        }

        // Build visitForm from Yjs data, using initialVisit as base for any missing fields
        const buildVisitForm = () => {
          const data = encounterData.toJSON();
          const apiVisit = initialVisitRef.current;

          // Merge encounterInfo - prefer Yjs, fallback to API
          let mergedEncounterInfo = data.encounterInfo || apiVisit?.encounterInfo;

          // Merge bodypicker from API if Yjs doesn't have it
          const yjsComplaints = data.encounterInfo?.complaints;
          const apiComplaints = apiVisit?.encounterInfo?.complaints;
          const yjsHasComplaints = Array.isArray(yjsComplaints) && yjsComplaints.length > 0;
          const apiHasComplaints = Array.isArray(apiComplaints) && apiComplaints.length > 0;
          // Yjs explicitly has a complaints array (even if empty) — means the sync
          // server has set it. Only fall back to API when Yjs has no encounterInfo
          // at all (initial load before sync data arrives).
          const yjsHasExplicitComplaints = Array.isArray(yjsComplaints);

          if (yjsHasComplaints && apiHasComplaints) {
            const apiComplaintsById = new Map(apiComplaints.map((c: any) => [c.id, c]));

            mergedEncounterInfo = {
              ...data.encounterInfo,
              complaints: yjsComplaints.map((yjsComplaint: any) => {
                const apiComplaint = apiComplaintsById.get(yjsComplaint.id);

                if (!yjsComplaint.bodypicker && apiComplaint?.bodypicker) {
                  return {
                    ...yjsComplaint,
                    bodypicker: apiComplaint.bodypicker,
                    bodypickerDerived: apiComplaint.bodypickerDerived,
                  };
                }

                return yjsComplaint;
              }),
            };
          } else if (!yjsHasExplicitComplaints && apiHasComplaints && apiVisit?.encounterInfo) {
            // Yjs has no encounterInfo.complaints at all (not even an empty array),
            // meaning sync data hasn't arrived yet — use API as fallback.
            mergedEncounterInfo = {
              ...(data.encounterInfo || {}),
              ...apiVisit.encounterInfo,
            };
          }

          // Care plan fields from API if Yjs doesn't have them
          if (apiVisit?.encounterInfo) {
            const yjsEncounterInfo = data.encounterInfo || {};
            // Treat `visitTimes: []` in Yjs as "missing" — see seeding useEffect
            // above for rationale. Keeps UI from rendering an empty schedule when
            // the DB still has populated CarePlanVisit rows.
            const yjsHasPopulatedVisitTimes =
              Array.isArray(yjsEncounterInfo.visitTimes) && yjsEncounterInfo.visitTimes.length > 0;
            const yjsVisitTimesUserCleared = yjsEncounterInfo.visitTimesClearedByUser === true;
            const shouldUseYjsVisitTimes = yjsHasPopulatedVisitTimes || yjsVisitTimesUserCleared;
            mergedEncounterInfo = {
              ...mergedEncounterInfo,
              goal: yjsEncounterInfo.goal ?? apiVisit.encounterInfo.goal,
              visitNumber:
                yjsEncounterInfo.visitNumber ??
                apiVisit.encounterInfo.visitNumber ??
                apiVisit?.visitNumber,
              nextReEval: yjsEncounterInfo.nextReEval ?? apiVisit.encounterInfo.nextReEval,
              visitTimes: shouldUseYjsVisitTimes
                ? Array.isArray(yjsEncounterInfo.visitTimes)
                  ? yjsEncounterInfo.visitTimes
                  : []
                : apiVisit.encounterInfo.visitTimes,
              notes: yjsEncounterInfo.notes ?? apiVisit.encounterInfo.notes,
              nextRequestedEncounterDate:
                yjsEncounterInfo.nextRequestedEncounterDate ??
                apiVisit.encounterInfo.nextRequestedEncounterDate,
              totalVisits: yjsEncounterInfo.totalVisits ?? apiVisit.encounterInfo.totalVisits,
              // surveysToDocument is API-authoritative: getFullEncounter computes it
              // from the provider's SOAP preferences while preserving any user-toggled
              // values already saved. Yjs starts with {} from visit creation and would
              // cause all "Document Survey" checkboxes to default to unchecked.
              surveysToDocument: apiVisit.encounterInfo.surveysToDocument,
              soapNoteDetailPreference: apiVisit.encounterInfo.soapNoteDetailPreference,
            };
          } else if (apiVisit?.visitNumber) {
            mergedEncounterInfo = {
              ...mergedEncounterInfo,
              visitNumber: apiVisit.visitNumber,
            };
          }

          // Merge complaintSummaries - API is authoritative for survey completion
          const mergedComplaintSummaries = (() => {
            const yjsSummaries = data.complaintSummaries || {};
            const apiSummaries = apiVisit?.complaintSummaries || {};
            let needsYjsUpdate = false;
            const merged = { ...yjsSummaries };

            // Build set of current complaint IDs from the merged encounterInfo.
            // Only backfill API summaries for complaints that still exist in the
            // encounter — otherwise deleted complaint summaries get resurrected.
            const currentComplaintIds = new Set(
              (mergedEncounterInfo?.complaints || [])
                .map((c: { id?: string }) => c.id)
                .filter(Boolean)
            );

            for (const [complaintId, apiSummary] of Object.entries(apiSummaries)) {
              // Skip summaries for complaints that no longer exist in the encounter
              if (!currentComplaintIds.has(complaintId)) continue;

              const yjsSummary = yjsSummaries[complaintId];
              const typedApiSummary = apiSummary as { placeholder?: boolean } | undefined;

              if (!yjsSummary) {
                merged[complaintId] = apiSummary;
                needsYjsUpdate = true;
              } else if (
                typedApiSummary?.placeholder === false &&
                yjsSummary?.placeholder === true
              ) {
                merged[complaintId] = apiSummary;
                needsYjsUpdate = true;
              }
            }

            if (needsYjsUpdate && encounterData) {
              encounterData.set('complaintSummaries', merged);
            }

            return merged;
          })();

          const topLevelVisitNumber = mergedEncounterInfo?.visitNumber ?? apiVisit?.visitNumber;

          // The API data is the authoritative source of truth for 'signed' state,
          // but we support a temporary 'signature event override' so encounters in other clients lock/unlock immediately while we refetch the API state.
          const { effectiveSigned, effectiveSignedAt } = getRealTimeSignedState({
            apiSigned: apiVisit?.signed,
            apiSignedAt: apiVisit?.signedAt,
            yjsSigned: data.signed,
            yjsSignedAt: data.signedAt,
            signatureMeta: data._signature as EncounterSignatureMetadata | undefined,
          });

          return {
            ...apiVisit,
            ...data,
            id: encounterId,
            encounterInfo: mergedEncounterInfo,
            visitNumber: topLevelVisitNumber,
            complaintSummaries: mergedComplaintSummaries,
            detail: resolveVisitDetail({ apiVisit, yjsVisit: data }),
            // API-authoritative fields (not stored in Yjs as source of truth)
            // version: API wins when available, fall back to Yjs for fast rendering
            version: apiVisit?.version !== undefined ? apiVisit.version : data.version,
            // signed/signedAt are workflow state. The REST API is the source of truth,
            // but we allow a short-lived "signature event override" (via `_signature`)
            // so other collaborators lock/unlock immediately while we refetch API server state.
            signed: effectiveSigned,
            signedAt: effectiveSignedAt,
            signedByUserId: apiVisit?.signedByUserId ?? null,
            signatureDetails: EncounterSignatureDetails.fromJSON(apiVisit?.signatureDetails),
            surveys: apiVisit?.surveys,
            inProgressComplaintSurveys: apiVisit?.inProgressComplaintSurveys,
            interimSurveyId: apiVisit?.interimSurveyId,
            reevaluationSurveyId: apiVisit?.reevaluationSurveyId,
            // Identity is API-authoritative: after a patient merge the doc
            // can still hold the merged-away patientId (ticket 12662309520).
            patientId: apiVisit?.patientId ?? data.patientId,
            clinicId: apiVisit?.clinicId ?? data.clinicId,
            // Yjs-first, same pattern as `_signature` above for signed/signedAt:
            // a collaborative op (reset-care-plan / cancel-reset-care-plan) repoints
            // this mid-session and broadcasts the new chain root through Yjs so other
            // connected clients see it without waiting for a REST refetch. Fall back
            // to REST only when Yjs has no value yet.
            carePlanId: data.carePlanId ?? apiVisit?.carePlanId,
            lifetimeVisits: apiVisit?.lifetimeVisits,
            recordings: apiVisit?.recordings,
          };
        };

        // Set up provider and encounterData, but DON'T set hasLocalData yet.
        // hasLocalData gates UI rendering — setting it before any data arrives
        // (sync server or API) causes a blank note flash on hard refresh.
        // It will be set to true by the observer (when sync data arrives)
        // or by the initialVisit useEffect (when API data arrives).
        const builtForm = buildVisitForm();
        const hasData =
          encounterData.size > 0 ||
          (initialVisitRef.current && Object.keys(initialVisitRef.current).length > 0);

        setState(prev => ({
          ...prev,
          provider,
          ydoc,
          encounterData,
          visitForm: builtForm,
          liveDetail: encounterData.get('detail') as VisitDetail | undefined,
          hasLocalData: !!hasData,
        }));

        // Subscribe to changes and update visitForm automatically
        // Use observeDeep to catch nested changes (e.g., vitals, plan, etc.)
        // Track last serialized form to avoid unnecessary re-renders
        let lastVisitFormJson = '';

        const observer = () => {
          if (isCleanedUp) return;

          const newVisitForm = buildVisitForm();
          const newLiveDetail = encounterData.get('detail') as VisitDetail | undefined;

          // Compare serialized JSON to avoid re-render when data hasn't actually changed.
          // liveDetail is part of the comparison: on a signed note visitForm.detail is
          // the row, so a doc-only detail change would otherwise never reach state.
          const newJson = JSON.stringify({ visitForm: newVisitForm, liveDetail: newLiveDetail });

          if (newJson === lastVisitFormJson) {
            return;
          }

          lastVisitFormJson = newJson;

          setState(prev => ({
            ...prev,
            visitForm: newVisitForm,
            liveDetail: newLiveDetail,
            // Sync server sent data — safe to show UI now
            hasLocalData: true,
          }));
        };

        encounterData.observeDeep(observer);

        observerUnsubscribe = () => {
          encounterData.unobserveDeep(observer);
        };

        // Check if cleanup was called while we were initializing (async race)
        if (isCleanedUp) {
          cleanup();

          return;
        }

        // Set awareness (user presence)
        provider.setAwarenessField('user', {
          userId: effectiveUserId,
          timestamp: Date.now(),
        });
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error : new Error('Failed to initialize'),
        }));
      }
    };

    doIt();

    // Cleanup on unmount
    return () => {
      isCleanedUp = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, encounterId, websocketUrl, documentName]); // Callbacks intentionally omitted to prevent re-initialization

  /**
   * Patch the API snapshot (initialVisitRef) so buildVisitForm's merge
   * logic sees updated data and doesn't overwrite Yjs with stale values.
   * Used after clearSurvey to update complaint summaries, survey IDs, and prId.
   */
  const patchInitialVisit = useCallback((patch: Partial<Visit>) => {
    if (initialVisitRef.current) {
      initialVisitRef.current = {
        ...initialVisitRef.current,
        ...patch,
      };
    }
  }, []);

  // Ref mirror of state.encounterData so the debounced-write callbacks keep
  // a stable identity (needed by pagehide listeners and effect cleanup).
  const encounterDataRef = useRef<Y.Map<any> | null>(null);

  // Single serialization path for every field write (updateField and
  // writeFieldNow differ only in where the map comes from). Serialize to
  // plain JSON before storing in Yjs to match initialization behavior:
  // nested arrays (e.g., complaintIds) stay plain JS arrays rather than
  // Yjs internal types (Y.Array/Y.Map), which would break .includes() on
  // retrieval.
  const serializeAndSet = useCallback((map: Y.Map<any>, key: string, value: any) => {
    try {
      const serialized =
        key === 'encounterInfo'
          ? sanitizeEncounterInfo(value as Record<string, unknown>)
          : JSON.parse(JSON.stringify(value));

      map.set(key, serialized);
    } catch (e) {
      // Key + error name only — the value can be PHI
      console.error('[collab] field write failed', key, (e as Error)?.name);
    }
  }, []);

  useEffect(() => {
    encounterDataRef.current = state.encounterData;
  }, [state.encounterData]);

  /**
   * Immediate single-key write. Same serialization as updateField, but reads
   * the map from a ref so callers keep a stable identity.
   */
  const writeFieldNow = useCallback(
    (key: string, value: any) => {
      const map = encounterDataRef.current;

      if (!map) {
        return;
      }

      serializeAndSet(map, key, value);
    },
    [serializeAndSet]
  );

  const { updateFieldDebounced, flushPendingWrites } = useMemo(
    () =>
      createDebouncedFieldWrites({
        writeField: writeFieldNow,
        // One Yjs transaction: multiple flushed keys fire observers once
        transact: fn => {
          const ydoc = ydocRef.current;

          if (ydoc) {
            ydoc.transact(fn);
          } else {
            fn();
          }
        },
      }),
    [writeFieldNow]
  );

  // Flush pending writes when the page is hidden or torn down. pagehide +
  // visibilitychange(hidden) because beforeunload is unreliable on iOS Safari.
  useEffect(() => {
    const flushOnHide = () => {
      flushPendingWrites();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushPendingWrites();
      }
    };

    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', flushOnHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // Unmount: don't lose the last ≤1s of typing
      flushPendingWrites();
    };
  }, [flushPendingWrites]);

  /**
   * Wait until the sync server has acked all local Yjs updates (bounded).
   * Operations like sign-note read the SERVER's doc over HTTP, racing the
   * websocket delivery of a just-flushed write — without this, the last
   * ≤1s of typing can be missing from a permanently signed note on a slow
   * connection. Resolves false on timeout; sendOperation then uses its REST
   * fallback, which carries the content in-band.
   */
  const awaitServerSync = useCallback((timeoutMs = 3000): Promise<boolean> => {
    const provider = providerRef.current;

    if (!provider || !provider.hasUnsyncedChanges) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>(resolve => {
      const done = (synced: boolean) => {
        clearTimeout(timer);
        provider.off('unsyncedChanges', check);
        resolve(synced);
      };

      const check = () => {
        if (!provider.hasUnsyncedChanges) {
          done(true);
        }
      };

      const timer = setTimeout(() => done(false), timeoutMs);

      provider.on('unsyncedChanges', check);
      // Drained between the guard above and listener registration
      check();
    });
  }, []);

  /**
   * Update a field in the encounter data
   */
  const updateField = useCallback(
    (key: string, value: any) => {
      if (!state.encounterData) {
        return;
      }

      serializeAndSet(state.encounterData, key, value);
    },
    [state.encounterData, serializeAndSet]
  );

  /**
   * Get a field from the encounter data
   */
  const getField = useCallback(
    (key: string): any => {
      if (!state.encounterData) {
        return undefined;
      }

      return state.encounterData.get(key);
    },
    [state.encounterData]
  );

  /**
   * Update a single sub-key inside the encounter `detail` object.
   *
   * Reads the current `detail` live from the Y.Map at call time (not from a React
   * closure), merges the new sub-key on top, and writes the merged object back.
   * This avoids the stale-closure clobber where a writer whose callback captured
   * an older `visitForm.detail` would overwrite sibling sub-keys added since.
   *
   * Use this for any single-sub-key write (tests, modalities, exercises, vitals).
   * For multi-sub-key atomic writes, read `getField('detail')` inline and call
   * `updateField('detail', merged)` directly instead of calling this helper twice.
   *
   * Clear semantics: passing `value: undefined` removes the key from `detail`.
   * `updateField` JSON-round-trips its input before writing to Yjs, which drops
   * undefined properties — so `updateDetailField('vitals', undefined)` stores
   * `detail` without a `vitals` key, matching the intuition that "set to
   * undefined" means "clear." `planStepDetailDiff` relies on the same behavior.
   */
  const updateDetailField = useCallback(
    (key: keyof VisitDetail, value: unknown) => {
      const currentDetail = (getField('detail') as VisitDetail | undefined) ?? {};
      updateField('detail', { ...currentDetail, [key]: value });
    },
    [getField, updateField]
  );

  /**
   * Delete a field from the encounter data
   */
  const deleteField = useCallback(
    (key: string) => {
      if (!state.encounterData) {
        return;
      }

      state.encounterData.delete(key);
    },
    [state.encounterData]
  );

  /**
   * Send an operation to the sync server when connected, falling back to
   * the Lambda-based REST API when not connected or on network/5xx errors.
   * 4xx errors (auth/validation) are re-thrown since the Lambda would reject
   * them too.
   *
   * Contract: sign-like operations (ones the sync server answers by reading
   * its Yjs doc) MUST supply a fallback that carries the full note content
   * in-band — an awaitServerSync timeout routes to the fallback precisely
   * because the server's doc can't be trusted to be current.
   */
  const sendOperation = useCallback(
    async <T = unknown>(args: {
      operation: Omit<SyncOperation, 'encounterId' | 'clinicId' | 'patientId'> & {
        encounterId?: string;
        clinicId?: number;
        patientId?: string;
      };
      fallback: () => Promise<T>;
    }): Promise<T> => {
      const { operation, fallback } = args;

      // Flush so a pending debounced write isn't missing from the result
      flushPendingWrites();

      // Only sign/unsign read the server-side Yjs doc, and the HTTP op races
      // the websocket delivery of the flush — wait for the server's ack.
      // Skipped offline (no socket to drain; the REST fallback below carries
      // the content in-band anyway) and for all other operation types.
      if (
        state.isConnected &&
        (operation.type === 'sign-note' || operation.type === 'unsign-note')
      ) {
        let synced = await awaitServerSync();

        if (synced) {
          // Keystrokes during the wait may sit pending (unflushed), invisible
          // to hasUnsyncedChanges — flush and drain once more so they're in
          // the doc the server signs. Bounded at one retry.
          flushPendingWrites();

          if (providerRef.current?.hasUnsyncedChanges) {
            synced = await awaitServerSync();
          }
        }

        if (!synced) {
          // Connected but the server never acked the flush (socket behind or
          // wedged) — signing via the sync server would read a stale doc.
          // The REST fallback sends the content in-band instead.
          return fallback();
        }
      }

      const fullOperation = {
        ...operation,
        encounterId: operation.encounterId || encounterId,
        clinicId: operation.clinicId || (state.visitForm?.clinicId as number),
        patientId: operation.patientId || (state.visitForm?.patientId as string),
      } as SyncOperation;

      if (state.isConnected) {
        try {
          const result = await sendSyncOperation<T>(fullOperation);

          return result.data as T;
        } catch (err: any) {
          const status = err?.response?.status;

          if (status && status >= 400 && status < 500) {
            throw err;
          }
        }
      }

      return fallback();
    },
    [
      encounterId,
      state.isConnected,
      state.visitForm?.clinicId,
      state.visitForm?.patientId,
      flushPendingWrites,
      awaitServerSync,
    ]
  );

  /**
   * Get all data as a plain JavaScript object
   */
  const getAllData = useCallback((): Record<string, any> => {
    if (!state.encounterData) {
      return {};
    }

    return state.encounterData.toJSON();
  }, [state.encounterData]);

  // Track pending subscriptions that were requested before encounterData was ready
  // These will be activated once encounterData becomes available
  const pendingSubscriptionsRef = useRef<
    Map<string, { key: string; callback: (value: any) => void; unsubscribe: (() => void) | null }>
  >(new Map());

  // When encounterData becomes available, activate any pending subscriptions
  useEffect(() => {
    if (!state.encounterData) return;

    const pending = pendingSubscriptionsRef.current;
    const entries = Array.from(pending.entries());

    for (const [, subscription] of entries) {
      if (!subscription.unsubscribe) {
        const observer = () => {
          const value = state.encounterData?.get(subscription.key);
          subscription.callback(value);
        };

        state.encounterData.observeDeep(observer);

        subscription.unsubscribe = () => {
          state.encounterData?.unobserveDeep(observer);
        };
      }
    }
  }, [state.encounterData]);

  /**
   * Subscribe to changes on a specific field.
   * If encounterData is not yet initialized, the subscription will be queued
   * and activated once it becomes available. This prevents memory leaks from
   * components that subscribe before the Y.js document is ready.
   */
  const subscribeToField = useCallback(
    (key: string, callback: (value: any) => void): (() => void) => {
      const subscriptionId = `${key}-${Date.now()}-${Math.random()}`;

      if (!state.encounterData) {
        // Queue the subscription for when encounterData becomes available
        pendingSubscriptionsRef.current.set(subscriptionId, {
          key,
          callback,
          unsubscribe: null,
        });

        // Return a cleanup function that removes from pending OR unsubscribes if activated
        return () => {
          const subscription = pendingSubscriptionsRef.current.get(subscriptionId);

          if (subscription?.unsubscribe) {
            subscription.unsubscribe();
          }

          pendingSubscriptionsRef.current.delete(subscriptionId);
        };
      }

      const observer = () => {
        const value = state.encounterData?.get(key);
        callback(value);
      };

      // Use observeDeep to catch nested changes
      state.encounterData.observeDeep(observer);

      return () => {
        state.encounterData?.unobserveDeep(observer);
      };
    },
    [state.encounterData]
  );

  /**
   * Set an awareness field (for tracking user state like open modals)
   */
  const setAwarenessField = useCallback((key: string, value: any) => {
    if (providerRef.current) {
      providerRef.current.setAwarenessField(key, value);
    }
  }, []);

  /**
   * Force reconnect - also resets failure count to allow recovery after max failures
   */
  const reconnect = useCallback(() => {
    if (providerRef.current) {
      // Reset failure count to allow reconnection after max failures
      failureCountRef.current = 0;
      // Clear any error state
      setState(prev => ({ ...prev, error: null }));
      providerRef.current.connect();
    }
  }, []);

  /**
   * Disconnect
   */
  const disconnect = useCallback(() => {
    if (providerRef.current) {
      providerRef.current.disconnect();
    }
  }, []);

  // Memoize the return value to prevent unnecessary re-renders of context consumers
  // Only create a new object reference when state or methods actually change
  return useMemo(() => {
    return {
      // State
      ...state,

      // Methods
      updateField,
      updateFieldDebounced,
      flushPendingWrites,
      updateDetailField,
      getField,
      deleteField,
      getAllData,
      subscribeToField,
      setAwarenessField,
      patchInitialVisit,
      reconnect,
      disconnect,
      sendOperation,
    };
  }, [
    state,
    updateField,
    updateFieldDebounced,
    flushPendingWrites,
    updateDetailField,
    getField,
    deleteField,
    getAllData,
    subscribeToField,
    setAwarenessField,
    patchInitialVisit,
    reconnect,
    disconnect,
    sendOperation,
  ]);
}
